/**
 * Passport data slice (P7 staff API, `api/src/modules/passport/routes.js`): the vehicle passport panel, the vehicle's
 * public passport link (issue / transfer / revoke — P1 `core.access_links` scope `passport`), share toggles, and the
 * warranty lookup by VIN last 6. Shapes match P7 `staff.vehiclePassport()` / `public.buildPassport()` exactly.
 * Vehicle search itself uses the shared customer/vehicle reads in core/entities.ts.
 */
import { APP, type Http } from '@app/core/http';

export interface PassportWarranty { label: string; startsAt: string; expiresAt: string; expiresOdometer: number | null; status: 'active' | 'expired' | 'void' | 'claimed' | string; expiredBy: 'date' | 'miles' | null }
export interface PassportEntry {
  jobNo: string; completedAt: string; odometer: number | null; services: string[]; totalCents: number | null; tech: string | null;
  parts: { description: string; brand: string | null; verifiedCode: string | null }[];
  photos: { url: string; kind: 'before' | 'after' | 'other' }[];
  warranties: PassportWarranty[];
}
export interface NextDue { service: string; dueOn: string | null; dueMiles: number | null; status: 'ok' | 'due_soon' | 'overdue' }
export interface VehiclePassport {
  business: { name: string; phone: string | null; logoUrl: string | null; color: string | null; timezone: string } | null;
  vehicle: { year: number | null; make: string | null; model: string | null; vin6: string | null; unitNo: string | null };
  odometer: number | null;
  health: { score: number; label: string };
  nextDue: NextDue[];
  activeWarranties: number;
  entries: PassportEntry[];
  showPrices: boolean;
  settings: { show_prices: boolean; show_photos: boolean };
  /** The live public link, if any. Its URL is only returned once, when minted (only a hash is stored). */
  link: { id: string; created_at: string } | null;
}
export interface WarrantyMatch {
  id: string; job_id: string; job_no: string | null; vehicle_id: string; year: number | null; make: string | null; model: string | null;
  vin: string | null; label: string; expires_at: string; expires_odometer: number | null; state: string; days_left: number | null;
  miles_left: number | null; customer: string | null; terms: string | null;
}
export interface Toggles { show_prices?: boolean; show_photos?: boolean }

export interface Api {
  vehicle(vehicleId: string): Promise<VehiclePassport>;
  /** Mint (or re-mint: the old link stops working) → the URL, shown once. `transfer` = new owner. */
  issueLink(vehicleId: string, o: { transfer?: boolean; key: string }): Promise<{ url: string }>;
  revokeLink(vehicleId: string, key: string): Promise<{ revoked: number }>;
  setToggles(vehicleId: string, t: Toggles): Promise<{ settings: { show_prices: boolean; show_photos: boolean } }>;
  lookup(vin6: string): Promise<WarrantyMatch[]>;
}

const P = `${APP}/passport`;
const id = encodeURIComponent;

export const live = (http: Http): Api => ({
  vehicle: (v) => http.get<VehiclePassport>(`${P}/vehicles/${id(v)}`),
  issueLink: (v, o) => http.post<{ url: string }>(`${P}/vehicles/${id(v)}/${o.transfer ? 'transfer' : 'link'}`, {}, { idempotencyKey: o.key }),
  revokeLink: (v, key) => http.post<{ revoked: number }>(`${P}/vehicles/${id(v)}/revoke`, {}, { idempotencyKey: key }),
  setToggles: (v, t) => http.patch(`${P}/vehicles/${id(v)}/settings`, t),
  lookup: (vin6) => http.get<{ matches: WarrantyMatch[] }>(`${P}/warranty/lookup`, { query: { vin6 } }).then((r) => r.matches),
});
